import { splitOptimizePayload, toBackendRoutes, UNASSIGNED_BUCKETS } from './linkedStops'

/**
 * True when every in-scope route (non-locked, non-PreRoute, matching
 * selectedVehicles filter) contains zero non-HUB stops after exclusion.
 * Uses the same scope rules as `splitOptimizePayload` / `oversizedGroupsInOptimizeSet`.
 */
export function isPostExclusionPayloadEmpty(payloadRoutes: any[], selectedVehicles: string[]): boolean {
  const vehicleFilter =
    selectedVehicles && selectedVehicles.length ? new Set(selectedVehicles) : null
  for (const r of payloadRoutes || []) {
    if (!r || r.isTruckLocked) continue
    const vehicle = r.vehicle
    if (typeof vehicle === 'string' && vehicle.includes('PreRoute')) continue
    if (vehicleFilter && !vehicleFilter.has(vehicle) && !UNASSIGNED_BUCKETS.has(vehicle)) continue
    const hasNonHub = (r.stops || []).some((s: any) => s && s.orderType !== 'HUB')
    if (hasNonHub) return false
  }
  return true
}

export const buildOptimizePayload = (state: any, region: any, scopeVehicles?: string[]) => {
  const opt = state.optimizer
  const s = opt.optimizeSettings
  const routes = opt.routes.map((r: any) => r.vehicle === 'PreRoute' ? { ...r, isTruckLocked: true } : r)

  // scopeVehicles narrows which vehicles' oversized linked groups are quarantined.
  // For BULK optimize (submitOptimize) it is omitted → scope is [] (= every unlocked
  // vehicle). The vehicle filter only limits which vehicles the SOLVER routes onto
  // (the backend's `selectedVehicles` field); all ready orders — including those on
  // non-selected vehicles — still feed the optimization, so their >2-stop linked
  // groups must move to Unfit the same as the unfiltered case. Locked vehicles are
  // skipped inside oversizedGroupsInOptimizeSet, so a locked truck stays untouched.
  // For single-route optimize (optimizeSingleRoute) scopeVehicles is [vehicle] so
  // only the target vehicle's groups are excluded.
  const scope = scopeVehicles ?? []
  const { payloadRoutes, excludedStops } = splitOptimizePayload(routes, scope)
  const backendRoutes = toBackendRoutes(payloadRoutes)
  const payloadEmpty = isPostExclusionPayloadEmpty(payloadRoutes, scope)

  // The three leading keys are internal (stripped before the POST). Everything
  // after them IS the request body — the server parses it directly into
  // OptimizeCollection (gson.fromJson(body, OptimizeCollection.class)), whose
  // top-level fields are options / data / selectedVehicles / isOptimizingBulk /
  // projectId / … So this object must NOT be wrapped in an extra `data` layer,
  // or the server reads `data` as OptimizerData (routes/vehicles/drivers all
  // null) and isOptimizingBulk defaults false → wrong (TSP) branch + NPE.
  return {
    excludedStops,
    payloadEmpty,
    payloadRoutes,
    options: {
      minVisitsPerVehicle: s.minVisitsPerVehicle,
      optimizationMode: s.optimizationMode,
      startWorkingTime: s.startWorkingTime,
      endWorkingTime: s.endWorkingTime,
      avoidToll: s.avoidToll,
      polylines: true,
      traffic: 'normal',
    },
    data: {
      drivers: opt.drivers, routes: backendRoutes, vehicles: opt.vehicles,
      selectedClients: opt.selectedClients, distributionZones: opt.distributionZones ?? [], missingVehicles: opt.missingVehicles,
    },
    selectedVehicles: s.selectedVehicles,
    selectedClients: s.selectedClients,
    region,
    isOptimizingBulk: true,
    projectId: process.env.REACT_APP_PROJECT_ID,
  }
}
